import {
	ColumnState, GridApi, GridReadyEvent, RowEvent
} from '@ag-grid-community/core';
import { Component, HostBinding, Injector, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { FieldExpression, LuceneExpression, NodeExpression, RangeExpression, term } from 'lucene';
import { debounceTime, distinctUntilChanged, firstValueFrom, fromEvent, Subscription } from 'rxjs';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import moment from 'moment';

import { Batch, FieldOfBatch } from '../../../../order/models/batch';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { LoadingSpinnerCellComponent }
	from '../../../../shared/datacenter/cell-renderers/loading-spinner-cell.component';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../shared/datacenter/grid-options';
import { DatacenterType, UserDatacenterView } from '../../../../shared/models/datacenter-aggrid-view';
import { DatacenterFilterType } from '../../../../shared/models/datacenter-filters';
import { WEIGHT_OPTIONS } from '../../../../shared/models/list';
import { GROSS } from '../../../../shared/models/settings';
import { TranslationService } from '../../../../shared/services/translation.service';
import { OrderBy, OrderByDirection } from '../../../../shared/utils/constant';
import { truncateTwoDecimals } from '../../../../shared/utils/number.format';
import { ECallOffByBatchViewService } from '../../../services/ecall-off-by-batch-view-service';
import { ECallOffService } from '../../../services/ecall-off.service';
import { DialogEcallOffComponent } from '../../dialog-ecall-off/dialog-ecall-off.component';
import { MillSelectorMill, MillSelectorComponent } from '../../mill/mill-selector.component';
import {
	normalizeViewName,
	resolveViewFromParam
} from '../../../../shared/utils/datacenter-view-utils';
import { SlideButtonComponent } from '../../../../shared/components/slide-button/slide-button.component';
import { DatacenterToolHeaderComponent } from '../../../../shared/datacenter/datacenter-tool-header.component';
import { ColDefWithFields } from '../../../../shared/datacenter/column-type';
import { ContextDatacenter } from '../../../../shared/datacenter/types/context-datacenter';
import { WorkerService } from '../../../../shared/services/worker.service';
import { WorkflowStepContent } from '../../../../shared/components/workflow-steps/workflow-steps.component';

const LIMIT = 10_000;
const SORT_WAY = OrderByDirection.ASC;
const DAYS_IN_STOCK_FILTER_ID = 'daysInStock';
const resizeObservable = fromEvent(globalThis, 'resize');
@Component({
	selector: 'copo-step-material-picking',
	templateUrl: './step-material-picking.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './step-material-picking.component.css',
	imports: [
		MillSelectorComponent,
		NgIf,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule,
		MatIconModule,
		DatacenterToolHeaderComponent,
		SlideButtonComponent,
		AgGridModule
	]
})
export class StepMaterialPickingComponent implements OnInit, OnDestroy, WorkflowStepContent {
	@HostBinding('class.passthrough') readonly passthrough = true;

	showBatches = false;
	displayMills = false;
	noSelectedBatches = false;
	isSearchClick = false;
	mills?: MillSelectorMill[];
	count: number | undefined;
	gridApi?: GridApi<unknown>;
	resizeSub?: Subscription;
	readonly agGridModules = clientSideAgGridModules;
	togglePosition = true;
	selectedCount = 0;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	selectedRows?: Batch[];
	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription: Subscription;
	batchResults?: Batch[];
	exportInProgress = false;
	datacenterType: DatacenterType = 'calloff-by-batch';
	exportExcelName = 'Ecall-off-by-batch';
	contextDatacenter: ContextDatacenter;
	gridCallableBatchesOptions: CommonGridOptions<Batch>;

	dialogSubscription: Subscription = new Subscription();
	millSubscription: Subscription = new Subscription();
	batchCardSubscription: Subscription = new Subscription();
	selectedBachesSubscription: Subscription = new Subscription();
	sub?: Subscription;
	isLoading = true;
	ecallSub?: Subscription;
	quickSearchFilter?: LuceneExpression;
	viewFilter?: LuceneExpression;
	orderCardPrefilter?: LuceneExpression;
	statusGroupFilterValue?: string;
	requiredFields: readonly FieldOfBatch[] = [];

	callableBatches?: Batch[];
	filter?: LuceneExpression;
	externalFilter?: LuceneExpression;
	batchesViews: UserDatacenterView[];
	batchesCurrentView: UserDatacenterView;
	private isSyncingSelection = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly ecallOffService: ECallOffService,
		private readonly ecallOffViewService: ECallOffByBatchViewService,
		readonly workerService: WorkerService,
		private readonly translationService: TranslationService,
		readonly injector: Injector,

		private readonly snackBar: MatSnackBar,

		private readonly dialog: MatDialog
	) {
		this.contextDatacenter = new ContextDatacenter(this.datacenterType, this.injector);
		this.gridCallableBatchesOptions = new CommonGridOptions<Batch>({
			columnDefs: [],
			context: {
				onDisplay: (batch: Partial<Batch>) => {
					this.openBatchCard(batch);
				}
			},
			headerHeight: 70,
			loadingOverlayComponent: LoadingSpinnerCellComponent,
			multiSortKey: 'ctrl',
			rowData: this.batchResults,
			rowSelection: 'multiple',
			suppressRowClickSelection: true,
			translationService: this.translationService
		});
		this.millSubscription = this.ecallOffService.currentMills.subscribe(mills => {
			this.mills = mills;
		});
		this.selectedBachesSubscription = this.ecallOffService.currentSelectedBatchesStep1
			.subscribe((data: Batch[]) => {
				this.selectedRows = data;
				this.selectedCount = data.length;
			});
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(searchString => {
			if (this.gridApi) {
				if (searchString === '') {
					this.resetGrid();
					return;
				}
				this.quickSearchFilter = <LuceneExpression> this.createQuickSearchFilter(searchString);
				this.getCallableBatches();
				this.gridApi.sizeColumnsToFit();
			}
		});
		// get available user views for order item list
		const defaultView = this.ecallOffViewService.getDefaultView();
		this.batchesViews = this.ecallOffViewService.getViewList(defaultView);
		const queryParams = this.route.snapshot.queryParamMap;
		const viewParam = queryParams.get('view');
		this.batchesCurrentView = viewParam
			? this.resolveViewFromUrlParam(viewParam, defaultView)
			: this.ecallOffViewService.getCurrentView(defaultView);
		this.viewFilter = this.ecallOffViewService.convertViewToLuceneFilter(this.batchesCurrentView);
		this.updateRequiredFields();

		const navigationState = <Record<string, unknown> | undefined> this.router.getCurrentNavigation()?.extras.state;
		const historyState = <Record<string, unknown> | undefined> history.state;
		this.applyDaysInStockFilter(navigationState ?? historyState);
	}

	ngOnInit() {
		this.orderCardPrefilter = this.ecallOffService.consumeStep1CallableBatchesFilter();

		if (!this.mills || this.mills.length === 0) {
			this.getMills().catch((error: unknown) => {
				console.error(error);
			});
		} else {
			this.showBatches = true;
			this.getCallableBatches();
		}
	}

	getCallableBatches() {
		const selectedMillCodes = this.mills?.filter(m => m.selected).map(m => m.code);
		if (!selectedMillCodes || selectedMillCodes.length === 0) {
			this.batchResults = [];
			this.count = 0;
			this.gridApi?.setGridOption('rowData', this.batchResults);
			return;
		}
		const finalFilter = this.createFinalSearchFilter(this.quickSearchFilter, this.viewFilter);
		this.isLoading = true;
		if (this.gridApi && this.selectedRows && this.selectedRows.length > 0 && !this.togglePosition) {
			this.setSelectedRows();
			this.isLoading = false;
		} else if (this.togglePosition) {
			const orderBy: OrderBy<keyof Batch>[] = [new OrderBy('cdd', SORT_WAY)];
			this.ecallSub = this.ecallOffService.getCallableBatches(
				this.requiredFields,
				finalFilter,
				1,
				LIMIT,
				orderBy,
			).subscribe({
				next: batchData => {
					if (this.gridApi) {
						this.batchResults = batchData.results;
						this.isSyncingSelection = true;
						this.gridApi.setGridOption('rowData', this.batchResults);
						if (this.selectedRows && this.selectedRows.length > 0) {
							const selectedBatches = [...new Set(this.selectedRows)];
							this.gridApi.forEachNode(node => {
								for (const batch of selectedBatches) {
									if (batch.id === (<Batch>node.data).id) {
										node.setSelected(true);
									}
								}
							});
						}
						this.isSyncingSelection = false;
						this.count = this.batchResults.length;
					}
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('error requesting batches', err);
				}
			});
		}
	}

	createFinalSearchFilter(currentFilter?: LuceneExpression, viewFilter?: LuceneExpression) {
		let rootNode: FieldExpression | NodeExpression;
		const callableNode: FieldExpression = {
			field: 'callable',
			term: 'O',
			quoted: false
		};

		rootNode = callableNode;

		if (currentFilter) {
			rootNode = {
				left: rootNode,
				operator: 'AND',
				right: currentFilter,
				parenthesized: true
			};
		}

		if (this.orderCardPrefilter) {
			rootNode = {
				left: rootNode,
				operator: 'AND',
				right: this.orderCardPrefilter,
				parenthesized: true
			};
		}

		if (viewFilter) {
			rootNode = {
				left: rootNode,
				operator: 'AND',
				right: viewFilter,
				parenthesized: true
			};
		}

		if (this.externalFilter) {
			rootNode = {
				left: rootNode,
				operator: 'AND',
				right: this.externalFilter,
				parenthesized: true
			};
		}

		if (this.statusGroupFilterValue) {
			const statusGroupReadyToShipNode: FieldExpression = {
				field: 'statusGroup',
				term: this.statusGroupFilterValue,
				quoted: true
			};
			rootNode = {
				left: rootNode,
				operator: 'AND',
				right: statusGroupReadyToShipNode,
				parenthesized: true
			};
		}

		const millFilterNode = this.createInListFilter(
			'mill',
			this.mills?.filter(m => m.selected).map(m => m.code) ?? []
		);

		rootNode = {
			left: rootNode,
			operator: 'AND',
			right: millFilterNode,
			parenthesized: true
		};
		return rootNode;
	}

	setStepState(state: Record<string, unknown> | undefined) {
		this.applyDaysInStockFilter(state);
	}

	createQuickSearchFilter(textSearch: string) {
		const escapedTextFilter = `*${ term.escape(textSearch) }*`;

		const customerOrderNumberField: FieldExpression = {
			field: 'customerOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierOrderNumberField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const steelgradeDescriptionField: FieldExpression = {
			field: 'steelgrade',
			term: escapedTextFilter,
			quoted: false
		};

		const batchReferenceField: FieldExpression = {
			field: 'batchReference',
			term: escapedTextFilter,
			quoted: false

		};

		const conditionNode1 = {
			left: customerOrderNumberField,
			operator: 'OR',
			right: supplierOrderNumberField,
			parenthesized: true
		};

		const conditionNode2 = {
			left: batchReferenceField,
			operator: 'OR',
			right: steelgradeDescriptionField,
			parenthesized: true
		};

		return {
			left: conditionNode1,
			operator: 'OR',
			right: conditionNode2,
			parenthesized: true
		};
	}

	createInListFilter(fieldName: string, values: string[]) {
		let filterCondition: LuceneExpression;
		if (values.length === 1) {
			filterCondition = {
				field: fieldName,
				term: values[0],
				quoted: true
			};
		} else {
			filterCondition = {
				field: fieldName,
				left: {
					field: '<implicit>',
					term: values[0],
					quoted: true
				},
				operator: '<implicit>',
				right: {
					field: '<implicit>',
					term: values[1],
					quoted: true
				},
				parenthesized: true
			};

			let current = filterCondition;
			for (const mill of values.slice(2)) {
				current.right = {
					left: current.right,
					operator: '<implicit>',
					right: {
						field: '<implicit>',
						term: mill,
						quoted: true
					},
					parenthesized: false
				};
				current = current.right;
			}

			return filterCondition;
		}
		return filterCondition;
	}

	async onCustomizeView(view: UserDatacenterView) {
		this.ecallOffViewService.setViewToEdit(view);
		await this.router.navigate(['view'], { relativeTo: this.route });
	}

	async onViewChange(view: UserDatacenterView) {
		this.batchesCurrentView = view;
		this.viewFilter = this.ecallOffViewService.convertViewToLuceneFilter(view);
		this.updateRequiredFields();
		this.updateColumnDefinitions();
		this.updateFilters();
		if (this.togglePosition) {
			this.getCallableBatches();
		}
		await this.ecallOffViewService.saveCurrentView(view);
		await this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { view: normalizeViewName(view.name) },
			replaceUrl: true
		});
	}

	setSelectedRows() {
		if (!this.togglePosition && this.selectedRows && this.selectedRows.length > 0 && this.gridApi) {
			this.isSyncingSelection = true;
			this.gridApi.setGridOption('rowData', this.selectedRows);
			this.gridApi.forEachNode(node => {
				if (node.displayed) {
					node.setSelected(true);
				}
			});
			this.isSyncingSelection = false;
			this.count = this.selectedRows.length;
		}
	}

	setTogglePosition($event: boolean) {
		this.togglePosition = $event;
		if (!this.togglePosition && this.selectedRows && this.selectedRows.length > 0) {
			this.noSelectedBatches = false;
			this.setSelectedRows();
		}

		if (!this.togglePosition && this.selectedRows?.length === 0) {
			this.noSelectedBatches = true;
		} else if (this.togglePosition) {
			this.noSelectedBatches = false;
			this.getCallableBatches();
		}
	}

	async getMills() {
		return firstValueFrom(this.ecallOffService.getEcallMills()).then(batches => {
			const mills: MillSelectorMill[] = batches.map(batch => ({
				code: batch.mill,
				description: batch.millDescription,
				isAllowChangeOfShipto: batch.isAllowChangeOfShipto,
				selected: true
			}));
			this.onMillsChange(mills);
		}).catch((error: unknown) => {
			console.error('Error when getting mills', error);
		});
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.updateColumnDefinitions();
		this.updateFilters();
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	onRowSelected(event: RowEvent<'rowSelected'>) {
		if (this.isSyncingSelection) {
			return;
		}
		const changedBatch = <Batch | undefined>event.node.data;
		if (!changedBatch) {
			return;
		}

		const selectedBatchesById = new Map<string, Batch>(
			(this.selectedRows ?? []).map(batch => [batch.id, batch])
		);
		if (event.node.isSelected()) {
			selectedBatchesById.set(changedBatch.id, changedBatch);
		} else {
			selectedBatchesById.delete(changedBatch.id);
		}
		this.selectedRows = [...selectedBatchesById.values()];
		this.selectedCount = this.selectedRows.length;
		this.ecallOffService.setSelectedBatches(this.selectedRows);
	}

	updateColumnDefinitions() {
		if (!this.gridApi) {
			return;
		}
		const allColumns = <ColDefWithFields[]> this.ecallOffViewService.getAllColumnDefinitions();
		const definitions =
			this.ecallOffViewService.convertViewToColumnDefinitions(this.batchesCurrentView, allColumns);
		this.gridApi.setGridOption('columnDefs', definitions);
		this.gridApi.applyColumnState({ state: definitions });
		this.gridApi.sizeColumnsToFit();
	}

	/**
	 * Recompute the API field list from the active view so only the needed fields are fetched.
	 */
	updateRequiredFields() {
		this.requiredFields =
			<readonly FieldOfBatch[]> this.ecallOffViewService.getFieldsForView(this.batchesCurrentView);
	}

	updateFilters() {
		if (!this.gridApi) {
			return;
		}
		const newFilterModel = this.ecallOffViewService.convertViewToFilterModel(this.batchesCurrentView);
		this.gridApi.setFilterModel(newFilterModel ?? null);
		this.gridApi.onFilterChanged();
	}

	toggleSearch() {
		this.isSearchClick = true;
	}

	resetGrid() {
		this.isSearchClick = false;
		if (this.gridApi) {
			this.quickSearchFilter = undefined;
			this.getCallableBatches();
			this.gridApi.sizeColumnsToFit();
			this.count = this.batchResults ? this.batchResults.length : 0;
		}
	}

	onMillsChange(mills: MillSelectorMill[]) {
		this.selectedCount = 0;
		this.showBatches = true;
		this.mills = mills;
		this.ecallOffService.updateMills(this.mills);
		this.selectedRows = this.filterBatchesFromMills(this.selectedRows, mills, true);
		this.ecallOffService.setSelectedBatches(this.selectedRows);
		this.setTogglePosition(true);
	}

	openBatchCard(batch: Partial<Batch>) {
		if (!batch.id) {
			console.warn('Missing batch id to open batch card', { batch });
			return;
		}
		const url = `/order-tracking/batch/card/${ batch.id }`;
		open(url, '_blank');
	}

	createValidateMillsChange(): (mills: MillSelectorMill[]) => Promise<boolean> {
		// when passing callback to another component
		// 'this' reference the child component instead of the actual
		// so use a factory to keep reference of the actual component
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const self = this;
		return async (mills: MillSelectorMill[]) => {
			// if no edited batches, you can change
			// if selected batches mills are still in the selected mills, you can change
			// else ask user to confirm change because current editing will be lost
			const selectedBatchesFromUnselectedMills =
				self.filterBatchesFromMills(self.selectedRows, mills, false);
			if (selectedBatchesFromUnselectedMills.length === 0) {
				return true; // cause nothing to reset, so can change
			}
			return self.openMillChangeDialog();
		};
	}

	filterBatchesFromMills(batches: Batch[] | undefined, mills: MillSelectorMill[], selected: boolean) {
		const millCodes = new Set(mills.filter(mill => mill.selected === selected).map(mill => mill.code));
		return batches?.filter(batch => millCodes.has(batch.mill)) ?? [];
	}

	async openMillChangeDialog() {
		if (!this.mills || this.mills.length === 0 || this.selectedCount === 0) {
			return true;
		}
		const dialogRef = this.dialog.open(DialogEcallOffComponent, {
			data: {}
		});
		return <boolean> await firstValueFrom(dialogRef.afterClosed());
	}

	sumOfTons() {
		let sumOfTons = 0;
		if (this.selectedRows) {
			sumOfTons = this.selectedRows.reduce((previous, current) =>
				previous + current.qtyBatchGross, 0);
		}
		return sumOfTons.toFixed(3);
	}

	ngOnDestroy() {
		this.millSubscription.unsubscribe();
		this.batchCardSubscription.unsubscribe();
		this.dialogSubscription.unsubscribe();
		this.resizeSub?.unsubscribe();
		this.ecallSub?.unsubscribe();
	}

	getDataToExport() {
		const filteredResults: unknown[] = [];
		if (this.selectedRows && this.selectedRows.length > 0) {
			return this.selectedRows;
		} else if (this.gridApi) {
			this.gridApi.forEachNodeAfterFilterAndSort(rowNode => {
				filteredResults.push(rowNode.data);
			});
			if (filteredResults.length > 0) {
				return filteredResults;
			}
		}
		return this.batchResults ?? [];
	}

	async exportExcelData() {
		if (this.gridApi?.getColumnDefs()) {
			this.exportInProgress = true;
			const allColumns = <ColDefWithFields[]> this.ecallOffViewService.getAllColumnDefinitions();
			const definitions =
			this.ecallOffViewService.convertViewToColumnDefinitions(this.batchesCurrentView, allColumns)
				.filter(col => col.colId !== 'add' && col.colId !== 'display' && !col.hide);
			const formatedData = this.formatExportedData(this.getDataToExport(), definitions);
			const columnTypes = definitions.map(columnDef => columnDef.type);
			await this.exportToExcel(formatedData, columnTypes);
		}
	}

	formatExportedData(dataToExport: unknown[], columnDefs: (ColDefWithFields & ColumnState)[]) {
		const exportData = (<Batch[]>dataToExport).map(data => ({
			...data,
			producedLength: data.form === '04' && data.producedLength
				? truncateTwoDecimals(data.producedLength / 1000) : undefined
		}));
		return [
			columnDefs.map(columnDef => columnDef.headerName ?? 'Error'),
			...exportData.map(
				data => columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, data))
			)
		];
	}

	async exportToExcel(formatedDataToExport: string[][], columnTypes: (string | string[] | undefined)[]) {
		return new Promise<void>((resolve, _reject) => {
			// if you change this line make sure that you change similar line inside gulp file
			const worker = this.workerService.createWorkerExcel();
			const fileName = `${ this.exportExcelName }-${ moment().format('YYYY MM DD') }.xlsx`;
			worker.addEventListener('message', ({ data }: { data: string }) => {
				this.exportInProgress = false;
				const link = document.createElement('a');
				link.download = fileName;
				link.href = data;
				link.append(document.createTextNode(fileName));
				document.body.append(link);
				link.click();
				link.remove();
				worker.terminate();
				resolve();
				// let 5 seconds to the browser for downloading before revoking the url
				setTimeout(() => {
					URL.revokeObjectURL(data);
				}, 5000);
			});
			const sheets = [
				{
					fileName,
					rows: formatedDataToExport,
					workSheetName: this.exportExcelName,
					columnTypes
				}
			];
			worker.postMessage({
				sheets
			});
		});
	}

	private applyDaysInStockFilter(state?: Record<string, unknown>) {
		const statusGroup = state?.['statusGroup'];
		this.statusGroupFilterValue =
			typeof statusGroup === 'string' || typeof statusGroup === 'number'
				? String(statusGroup)
				: undefined;

		const daysInStockFrom = state?.['daysInStockFrom'];
		const daysInStockTo = state?.['daysInStockTo'];
		if (typeof daysInStockFrom !== 'string' && typeof daysInStockFrom !== 'number') {
			this.externalFilter = undefined;
			this.viewFilter = this.ecallOffViewService.convertViewToLuceneFilter(this.batchesCurrentView);
			return;
		}

		const normalizedDaysInStockFrom = String(daysInStockFrom);
		const normalizedDaysInStockTo =
			typeof daysInStockTo === 'string' || typeof daysInStockTo === 'number'
				? String(daysInStockTo)
				: '*';

		const daysInStockRange: RangeExpression = {
			field: 'daysInStock',
			term_min: normalizedDaysInStockFrom,
			term_max: normalizedDaysInStockTo,
			inclusive: 'both'
		};
		this.externalFilter = daysInStockRange;
		this.syncDaysInStockFilterState(normalizedDaysInStockFrom, normalizedDaysInStockTo === '*'
			? ''
			: normalizedDaysInStockTo);
	}

	private syncDaysInStockFilterState(from?: string, to?: string) {
		const sanitizedFilterState = (this.batchesCurrentView.filterState ?? [])
			.filter(filter => filter.id !== DAYS_IN_STOCK_FILTER_ID);

		const daysInStockFilter: DatacenterFilterType | undefined = from
			? {
				id: DAYS_IN_STOCK_FILTER_ID,
				values: [{ from, to: to ?? '' }]
			}
			: undefined;

		this.batchesCurrentView = {
			...this.batchesCurrentView,
			filterState: daysInStockFilter
				? [...sanitizedFilterState, daysInStockFilter]
				: sanitizedFilterState
		};

		this.viewFilter = this.ecallOffViewService.convertViewToLuceneFilter({
			...this.batchesCurrentView,
			filterState: sanitizedFilterState
		});
	}

	private resolveViewFromUrlParam(viewParam: string | null, defaultView: UserDatacenterView): UserDatacenterView {
		const res = resolveViewFromParam(viewParam, this.batchesViews, defaultView);
		if (!res.found) {
			this.snackBar.open(
				this.translationService.getTR('datacenter.view-not-found', { viewName: res.decodedViewName }),
				undefined,
				{ duration: 3000 }
			);
		}
		return res.view;
	}
}
